import { useMemo, useState } from 'react'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import { HStack, VStack } from '@musinsa/mwms-ds'
import styled from 'styled-components'
import { Divider } from '@/components/content/Divider'
import Badge from '@/components/content/Badge'
// mwms-ds 의 Text 는 kind 키 체계가 달라('12-bold') 사내 kind 를 못 알아보고 부모(제목 슬롯 16px bold)를 물려받는다.
// 형제 탭과 같은 크기로 나오려면 사내 Text 를 써야 한다.
import Text from '@/components/content/Text'
import GridTable from '@/components/table/GridTable'
import { GridTooltip } from '@/pages/settlement/SettlementManagement/components/GridTooltip'
import { PROMOTION_EXPORT_AUDIT } from '@/pages/settlement/SettlementManagement/consts/consts'
import { defaultColDef } from '@/components/table/grid_table_options'
import { Utils } from '@/utils/Utils'
import { isNil } from 'lodash-es'
import { GRID_SIZE } from '@/utils/grid/grid'
import { colorSystem, fontSystem } from '@/styles/theme'
import {
  useExportExcelPromotionSettlementLines,
  usePostPromotionSettlementDetails,
  usePostPromotionSettlementSearch,
} from '@/services/settlement/settlementPromotion/useSettlementPromotionQueries'
import { PromotionSettlementSummaryRow } from '@/services/settlement/settlementPromotion/types'
import { SETTLEMENT_DETAIL_SKU_VIEW_FLAG } from '@/services/settlement/settlementManagement/types'
import { useFeatureFlagBoolean } from '@/services/feature/useFeatureQueries'
import {
  buildLineQueryScope,
  buildPinnedRows,
  isPinnedRow as isPinned,
  isTotalRow as isTotal,
  pinnedAreaHeight,
  PINNED_ROW_HEIGHT,
  promotionDiscountValueText,
  promotionTierTooltipText,
  PromotionTabRow,
  settlementCycleOf,
} from '@/pages/settlement/SettlementManagement/utils/promotionTabRows'

interface Props {
  partnerId: string
  // 정산월 구간 (전월 26 ~ 당월 25) — 상위 파트너 정산 요약에서 내려받음
  settlementDateFrom?: string
  settlementDateTo?: string
  // LP9182 국가 필터 — mass 조회 API 에 국가 파라미터가 없어 응답 행의 country(anchor 파생)로 클라 필터
  country?: string
}

// 정산 상세 시안(02_파트너정산_프로모션할인_v2.html:326)의 문구 — 일반/기간.
// BE SettlementCycle.displayName 은 '일 단위/월 단위' 라 화면 문구가 다르다.
const SETTLEMENT_CYCLE_LABEL: Record<string, string> = {
  DAILY: '일반',
  MONTHLY: '기간',
}

const COUNTRY_LABEL: Record<PromotionSettlementSummaryRow['country'], string> = {
  KR: '한국',
  JP: '일본',
  GLOBAL: '글로벌',
}

function formatWon(value?: number | null) {
  if (isNil(value)) return '-'
  return `${Utils.getCommaNum(value)}원`
}

// 할인금액은 시안에서 음수·강조색으로 표기한다 (BE 는 양수로 내려준다)
function formatDiscount(value?: number | null) {
  if (isNil(value)) return '-'
  return value === 0 ? '0원' : `-${Utils.getCommaNum(value)}원`
}

/**
 * 긴 값은 열 폭에서 잘리고 `...` 로 끝난다. 전문은 마우스를 올리면 툴팁으로 본다 —
 * 줄바꿈으로 늘리면 행 높이가 들쭉날쭉해져 표가 읽기 어려워진다.
 */
const TRUNCATED: Pick<ColDef, 'cellClass' | 'tooltipValueGetter'> = {
  cellClass: 'truncated-cell',
  tooltipValueGetter: ({ value }) => (value === '-' ? undefined : value),
}

const RIGHT_ALIGNED: Pick<ColDef, 'headerClass' | 'cellClass'> = {
  headerClass: ['ag-right-aligned-header', 'no-divider'],
  cellClass: 'ag-right-aligned-cell',
}

// BE 상한(@Max 500). (파트너 · 정산월) 프로모션 수는 이보다 훨씬 적어 단일 페이지로 전량 조회한다.
const SUMMARY_PAGE_SIZE = 500

// 정산 주기 칩 색 — colorSystem 토큰에 없는 시안 색이라 여기 한 곳에 둔다 (시안 02_파트너정산_프로모션할인_v2.html:326)
const CYCLE_BADGE_COLOR = {
  MONTHLY: { background: '#f9f0ff', text: '#531dab' },
  DAILY: { background: '#e6f4ff', text: '#0958d9' },
} as const

/**
 * 형제 서비스 탭과 같은 행 높이·합계행 굵기.
 * 표 높이는 화면 안에 묶는다 — 라인이 한 페이지 100건이라 그냥 두면 페이지가 끝없이 길어진다.
 * GridTableWrap(자식 div) 높이가 auto 라 여기서 잡아 주지 않으면 표가 0px 로 접힌다.
 */
// 표 제목 줄 — 왼쪽 건수, 오른쪽 VAT 표기 (GridTable 은 제목을 한 덩어리로 받는다)
const TitleBar = styled(HStack)`
  width: 100%;
  align-items: center;
  justify-content: space-between;

  /* 형제 탭과 같이 색은 여기서 한 번만 준다 — 제목 줄에 항목이 늘어도 빠뜨릴 자리가 없다 */
  color: ${colorSystem.text.common.tertiary};
`

const TableWrap = styled(VStack)<{ $pinnedRowCount: number }>`
  /* 표는 탭 영역이 주는 높이를 그대로 쓴다 — 위(요약·탭)가 줄면 표가 그만큼 늘고, 늘면 줄어든다 */
  height: 100%;
  min-height: 0;

  > div {
    /* 탭 영역이 주는 높이만 쓴다 — 페이지가 스크롤되지 않게. 고정행 높이가 묶여 있어
       남는 공간은 전부 본문(품목 라인)이 가져간다 */
    height: 100%;
    min-height: 0;
  }

  /* GridTable 제목 슬롯이 span 이라 줄 전체로 안 펴진다 — 건수(왼쪽)·VAT(오른쪽) 배치를 위해 편다 */
  > div > div:first-child > span:first-child {
    display: block;
    flex: 1;
  }

  .ag-cell-wrapper {
    height: 52px;
  }

  /* 고정행은 본문보다 낮으므로 셀 래퍼도 같이 낮춘다 (52px 로 두면 아랫행으로 넘친다) */
  .ag-floating-top .ag-cell-wrapper {
    height: ${PINNED_ROW_HEIGHT}px;
  }

  /* 고정행은 합계 1행 + 프로모션 소계 N행이다. 프로모션이 많으면 본문 자리를 다 먹으므로
     MAX_VISIBLE_PINNED_ROWS 줄까지만 보이고 그 안에서 스크롤하게 둔다 */
  .ag-floating-top {
    /* AG Grid 가 행 수만큼 inline height·min-height 를 박으므로 둘 다 눌러야 한다.
       고정으로 두면 고정행이 3줄 미만일 때 남는 자리가 빈 띠로 보인다 */
    height: ${({ $pinnedRowCount }) => pinnedAreaHeight($pinnedRowCount)}px !important;
    min-height: 0 !important;
    /* .ag-floating-top 은 flex 아이템(flex-shrink:1)이라, min-height:0 상태에서 표 세로 공간이
       부족하면 height 를 무시하고 몇 px 로 짓눌린다 — 합계·소계행이 통째로 잘려 안 보인다 (LOGISPRD-11505).
       flex-shrink 를 막아 위 height 를 그대로 지킨다 */
    flex-shrink: 0;

    /* 실제로 넘치는 것은 안쪽 뷰포트라 스크롤도 거기 건다.
       스크롤바를 그리면 그 폭(15px)만큼 본문과 열이 어긋나고 스크롤바도 둘로 보여, 휠로만 굴린다 */
    .ag-floating-top-viewport {
      overflow-y: auto !important;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        width: 0;
        height: 0;
      }
    }

    * {
      ${fontSystem.Body_12_bold};
    }
  }

  /* 잘린 값은 말줄임으로 끝낸다 (전문은 툴팁) */
  .truncated-cell {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* 정산 주기 칩 — 셀 높이(52px)에 맞춰 알약 모양으로 세우고 글자를 가운데 둔다 */
  .cycle-badge {
    display: inline-flex;
    align-items: center;
    height: 24px;
    padding: 0 10px;
    border-radius: 999px;

    > * {
      padding-top: 0;
      font-weight: 600;
    }
  }

  /* 할인금액은 음수 표기 — 경고가 아니라 혜택이라 파랑(시안·SCM 포털과 같은 축) */
  .discount-amount {
    color: ${colorSystem.text.common.link};
  }
`

// 정산 주기·국가는 고정행(집계)과 라인 응답 양쪽에 있다 (LOGISPRD-11359) — 합계행만 비운다
const countryOf = (row?: PromotionTabRow) => (isTotal(row) ? undefined : row?.country)

// 열 순서·명칭은 시안(02_파트너정산_프로모션할인_v2.html:321) 기준.
// 시안에 있던 '할인 적용 단가'·'SKU ID' 는 화면에서 뺐다 (SG 지시 08-19).
// SKU ID 는 sku_id 노출 flag ON 에서만 다시 보인다 (LOGISPRD-14189, getPromotionColumns).
// 열 폭은 좁혀 두고, 필요하면 헤더 경계를 끌어 조절한다.
const columns: ColDef<PromotionTabRow>[] = [
  {
    colId: 'settlementDate',
    headerName: '적용일',
    width: 96,
    valueGetter: ({ data }) => {
      if (isTotal(data)) return '합계'
      if (isPinned(data)) return ''
      return data?.settlementDate ?? '-'
    },
  },
  {
    colId: 'promotionName',
    headerName: '프로모션명',
    width: 240,
    ...TRUNCATED,
    valueGetter: ({ data }) => (isTotal(data) ? '' : data?.promotionName ?? '-'),
  },
  {
    colId: 'serviceType',
    headerName: '서비스 항목',
    width: 112,
    valueGetter: ({ data }) => (isTotal(data) ? '' : data?.serviceType?.name ?? '-'),
  },
  {
    colId: 'discountType',
    headerName: '할인 방식',
    width: 84,
    valueGetter: ({ data }) => (isTotal(data) ? '' : data?.discountType?.name ?? '-'),
  },
  {
    colId: 'discountValue',
    headerName: '할인값',
    width: 88,
    ...RIGHT_ALIGNED,
    valueGetter: ({ data }) => promotionDiscountValueText(data),
    tooltipValueGetter: ({ data }) => promotionTierTooltipText(data),
  },
  {
    // 시안은 정산 주기를 칩으로 표기하고 일/월을 색으로 가른다.
    // 표기는 시안 문구(일별/기간) — BE enum SettlementCycle 의 displayName(일 단위/월 단위)과 다르다.
    colId: 'settlementCycle',
    headerName: '정산 주기',
    width: 84,
    valueGetter: ({ data }) => {
      const cycle = settlementCycleOf(data)
      return cycle ? SETTLEMENT_CYCLE_LABEL[cycle.code] ?? '-' : ''
    },
    cellRenderer: ({ value, data }: ICellRendererParams<PromotionTabRow>) => {
      if (!value || value === '-') return value ?? ''
      const color = settlementCycleOf(data)?.code === 'MONTHLY' ? CYCLE_BADGE_COLOR.MONTHLY : CYCLE_BADGE_COLOR.DAILY
      return <Badge className="cycle-badge" type="CUSTOM" text={value} backgroundColor={color.background} textColor={color.text} />
    },
  },
  {
    // 프로모션 부과 기준(anchor)의 국가 축 — 프로모션당 하나로 정해진다
    colId: 'country',
    headerName: '국가',
    width: 64,
    valueGetter: ({ data }) => {
      const country = countryOf(data)
      return country ? COUNTRY_LABEL[country] : ''
    },
  },
  {
    colId: 'brandName',
    headerName: '브랜드',
    width: 140,
    ...TRUNCATED,
    valueGetter: ({ data }) => {
      if (isPinned(data)) return ''
      return data?.brandName ? `${data.brandName}(${data.brandId})` : '-'
    },
  },
  {
    colId: 'productNumber',
    headerName: '상품번호',
    width: 96,
    valueGetter: ({ data }) => (isPinned(data) ? '' : data?.productNumber ?? '-'),
  },
  {
    colId: 'productName',
    headerName: '상품명',
    width: 260,
    ...TRUNCATED,
    valueGetter: ({ data }) => (isPinned(data) ? '' : data?.productName ?? '-'),
  },
  {
    colId: 'productOption',
    headerName: '옵션',
    width: 150,
    ...TRUNCATED,
    valueGetter: ({ data }) => (isPinned(data) ? '' : data?.productOption ?? '-'),
  },
  {
    colId: 'productBarcode',
    headerName: '바코드',
    width: 150,
    valueGetter: ({ data }) => (isPinned(data) ? '' : data?.productBarcode ?? '-'),
  },
  {
    colId: 'quantity',
    headerName: '수량',
    width: 76,
    ...RIGHT_ALIGNED,
    valueGetter: ({ data }) => (isNil(data?.quantity) ? '-' : Utils.getCommaNum(data!.quantity!)),
  },
  {
    colId: 'unitPrice',
    headerName: '단가',
    width: 88,
    ...RIGHT_ALIGNED,
    valueGetter: ({ data }) => (isPinned(data) ? '' : formatWon(data?.unitPrice)),
  },
  {
    colId: 'originAmount',
    headerName: '할인 전 금액',
    width: 112,
    ...RIGHT_ALIGNED,
    valueGetter: ({ data }) => formatWon(data?.originAmount),
  },
  {
    colId: 'discountAmount',
    headerName: '할인금액',
    width: 112,
    ...RIGHT_ALIGNED,
    cellClass: ['ag-right-aligned-cell', 'discount-amount'],
    valueGetter: ({ data }) => formatDiscount(data?.discountAmount),
  },
  {
    colId: 'discountedAmount',
    headerName: '할인 후 금액',
    width: 112,
    ...RIGHT_ALIGNED,
    valueGetter: ({ data }) => formatWon(data?.discountedAmount),
  },
]

// LOGISPRD-14189 — 라인의 sku_id. 합계·소계 고정행은 비운다
const skuIdColumn: ColDef<PromotionTabRow> = {
  colId: 'skuId',
  headerName: 'SKU ID',
  width: 150,
  valueGetter: ({ data }) => (isPinned(data) ? '' : data?.skuId ?? '-'),
}

// sku_id 노출 flag ON 이면 상품번호·옵션을 빼고 SKU ID 를 바코드 바로 뒤에 둔다. OFF 면 현행 그대로
export const getPromotionColumns = (isSkuViewEnabled: boolean = false): ColDef<PromotionTabRow>[] => {
  if (!isSkuViewEnabled) return columns
  return columns.flatMap((col) => {
    if (col.colId === 'productNumber' || col.colId === 'productOption') return []
    return col.colId === 'productBarcode' ? [col, skuIdColumn] : [col]
  })
}

/**
 * 파트너정산 상세 — "프로모션" 탭 (LOGISPRD-11360 · 시안 02_파트너정산_프로모션할인_v2.html:321)
 *
 * 표 하나에 전체 합계 → 프로모션별 소계 → 품목 라인 순으로 나온다. 합계·소계는 고정행이라
 * 라인 페이지를 넘겨도 자리를 지킨다. 행 클릭 드릴다운은 없다.
 *
 * 합계·소계는 집계 API(promotion_daily_settlement_summary 기간 합산), 라인은 통합 조회
 * API(원본 6종 UNION, LOGISPRD-11359)에서 온다. 국가 필터는 라인 응답에 국가 축이 없어
 * 소계행을 거른 뒤 그 프로모션들만 라인으로 요청해 맞춘다.
 */
export function PromotionDiscountTab({ partnerId, settlementDateFrom, settlementDateTo, country }: Props) {
  const [linePage, setLinePage] = useState(0)

  const { data: isSkuViewEnabled } = useFeatureFlagBoolean(SETTLEMENT_DETAIL_SKU_VIEW_FLAG.key, SETTLEMENT_DETAIL_SKU_VIEW_FLAG.objectKey)
  const columnDefs = useMemo(() => getPromotionColumns(isSkuViewEnabled), [isSkuViewEnabled])

  const rangeReady = !!settlementDateFrom && !!settlementDateTo

  // 합계·소계를 전체 기준으로 내려면 모든 프로모션이 한 번에 있어야 한다. (파트너 · 정산월) 조합의
  // 프로모션 수는 BE 상한(size 500)보다 훨씬 적으므로 단일 페이지로 받는다.
  const { data: summary, isLoading: isSummaryLoading } = usePostPromotionSettlementSearch(
    {
      settlementDateFrom: settlementDateFrom ?? '',
      settlementDateTo: settlementDateTo ?? '',
      partnerIds: [partnerId],
      page: 0,
      size: SUMMARY_PAGE_SIZE,
    },
    rangeReady,
  )

  // 상단 국가 필터(전 탭 공용 위치)와 표시 정합 — 미선택(undefined)은 전체
  const summaryRows = useMemo(() => (summary?.items ?? []).filter((row) => !country || row.country === country), [summary, country])

  const { onExportData, isLoading: isExcelLoading } = useExportExcelPromotionSettlementLines()

  const { promotionIds, enabled: scopeReady } = useMemo(() => buildLineQueryScope(summaryRows, Boolean(country)), [summaryRows, country])

  const { data: details } = usePostPromotionSettlementDetails(
    {
      promotionIds,
      settlementDateFrom: settlementDateFrom ?? '',
      settlementDateTo: settlementDateTo ?? '',
      partnerId,
      page: linePage,
      size: GRID_SIZE.SETTLEMENT,
    },
    rangeReady && scopeReady,
  )

  // 고정행 — 전체 합계 1행 + 프로모션별 소계 N행 (시안의 sum · psum)
  const pinnedRows = useMemo(() => buildPinnedRows(summaryRows), [summaryRows])

  return (
    <TableWrap full gap={24} style={{ flex: 1, minHeight: 0 }} $pinnedRowCount={pinnedRows.length}>
      <GridTable<PromotionTabRow>
        gridKey="BL03_PROMO"
        title={
          // 왼쪽은 형제 탭과 같은 (현재 페이지 건수 | 전체 건수), 오른쪽은 시안의 'VAT 별도'
          <TitleBar>
            <HStack gap={8} align="center">
              <Text kind="Body_12_bold" color="inherit">
                {details?.lines.length ?? 0}
              </Text>
              <Divider variant="vertical" style={{ height: '8px' }} />
              <Text kind="Body_12_bold" color="inherit">
                {details?.pageInfo.totalCount ?? 0}
              </Text>
            </HStack>
            <Text kind="Body_12_bold" color="inherit">
              VAT 별도
            </Text>
          </TitleBar>
        }
        isSearchVisible={false}
        onExportData={() =>
          onExportData({
            promotionIds,
            settlementDateFrom: settlementDateFrom ?? '',
            settlementDateTo: settlementDateTo ?? '',
            partnerId,
          })
        }
        exportType="private"
        exportWithPassword={false}
        exportAudit={PROMOTION_EXPORT_AUDIT}
        // 라벨에 색을 고정하면 버튼이 비활성일 때도 진한 색으로 남는다 — 버튼의 :disabled 색을 받게 둔다
        exportButtonLabel={
          <Text kind="Body_14_medium" color="inherit">
            전체 다운로드
          </Text>
        }
        // 내려받는 중에는 다시 누를 수 없게 한다 — 같은 GET 이 중복으로 나간다
        disabledExport={!scopeReady || !details?.pageInfo.totalCount || isExcelLoading}
        // 고정행(합계·소계)은 autoHeight 대상이 아니라 행 높이를 직접 준다 — 안 주면 셀 내용이 겹친다
        // 고정행(합계·프로모션 소계)은 개수가 프로모션 수만큼 늘어난다 — 낮게 잡아 본문 자리를 남긴다
        getRowHeight={({ node }) => (node.rowPinned ? PINNED_ROW_HEIGHT : undefined)}
        defaultColDef={
          {
            ...defaultColDef,
            suppressMenu: true,
            autoHeight: true,
            tooltipComponent: GridTooltip,
          } as ColDef<PromotionTabRow>
        }
        tooltipShowDelay={0}
        enableBrowserTooltips={false}
        // 구간 툴팁이 여러 줄이라 기본 popupParent(.ag-root-wrapper) 안에서는 경계 보정에 걸려 잘릴 수 있다.
        // 프로모션 관리 목록이 같은 툴팁으로 겪은 전례를 따라 body 로 뺀다 (v2/components/PromotionManagementTable.tsx)
        gridOptions={{ popupParent: document.body }}
        // 라인 로딩까지 묶으면 페이지를 넘길 때마다 합계·소계 고정행이 오버레이에 가린다
        isLoading={isSummaryLoading || !rangeReady}
        rowData={details?.lines ?? []}
        pinnedTopRowData={pinnedRows}
        columnDefs={columnDefs}
        countPerPage={GRID_SIZE.SETTLEMENT}
        totalCount={details?.pageInfo.totalCount ?? 0}
        currentPage={linePage}
        onChangePage={setLinePage}
      />
    </TableWrap>
  )
}

export default PromotionDiscountTab
